import { useFlags } from "launchdarkly-react-client-sdk";

import { useAppSelector } from "app/hooks";
import {
  INBOX_TEXT_ROUTING_FEATURE,
  INBOX_V2_FEATURE
} from "constants/feature-flags";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { Practice } from "services/types/clinics.type";

export type TextRoutingGate = {
  /**
   * The LaunchDarkly flag alone. Gates the admin setup surfaces (Clinics →
   * Inbox owner column and bulk assign, the user's clinic-owner alert
   * preference), which admins use BEFORE any clinic has an owner.
   */
  isFlagOn: boolean;
  /**
   * Flag on AND Inbox V2 on AND the practice routes texts: some active clinic
   * has an inbox owner (`Practice.text_routing_active`, ENG-2223 — there is no
   * practice switch). Gates the Mine / My clinics / Unassigned tabs, the owner
   * context and the routing alerts; the routing views exist only in the V2
   * inbox. False => the app requests and renders exactly what it did before.
   */
  routingOn: boolean;
};

export const resolveTextRoutingGate = (
  flagValue: unknown,
  inboxV2Value: unknown,
  practice: Pick<Practice, "text_routing_active"> | null | undefined
): TextRoutingGate => {
  const isFlagOn = flagValue === true;
  return {
    isFlagOn,
    routingOn:
      isFlagOn &&
      Boolean(inboxV2Value) &&
      practice?.text_routing_active === true
  };
};

/**
 * The ONE place that combines the `inboxTextRouting` and `inboxV2Feature` flags
 * with the practice's `text_routing_active`. Read the gate through this hook; never read the
 * flag inline at a call site.
 */
export default function useTextRoutingEnabled(): TextRoutingGate {
  const flags = useFlags();
  const practice = useAppSelector(selectCurrentPractice);
  return resolveTextRoutingGate(
    flags[INBOX_TEXT_ROUTING_FEATURE],
    flags[INBOX_V2_FEATURE],
    practice
  );
}
